import { useEffect, useMemo, useState } from "react";
import {
  Edit3,
  Plus,
  ShieldCheck,
  Trash2,
  UserCog,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { AREA_BY_ID, RESTAURANT_BY_ID, ROLES, ROLE_LABEL } from "../constants";
import { addUser, loadUsers, removeUser, updateUser } from "../lib/storage";
import UserDialog from "./users/UserDialog";

export default function UsersPanel({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState({ open: false, user: null });
  const [confirm, setConfirm] = useState(null);
  const [tab, setTab] = useState("all");

  useEffect(() => { setUsers(loadUsers()); }, []);

  const grouped = useMemo(() => ({
    main: users.filter((u) => u.role === ROLES.MAIN),
    r1: users.filter((u) => u.restaurant === "restaurant_1"),
    r2: users.filter((u) => u.restaurant === "restaurant_2"),
  }), [users]);

  const save = (u, isEdit) => {
    setUsers(isEdit ? updateUser(u.id, u) : addUser(u));
    toast.success(isEdit ? "User updated" : "User added");
  };
  const del = (id) => { setUsers(removeUser(id)); toast("User removed"); };

  const showR1 = tab === "all" || tab === "restaurant_1";
  const showR2 = tab === "all" || tab === "restaurant_2";

  return (
    <section>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="inline-flex rounded-lg bg-white border border-[#E6E0D4] p-1">
          <UTab active={tab === "all"} onClick={() => setTab("all")}>All <Count n={users.length} /></UTab>
          <UTab active={tab === "restaurant_1"} onClick={() => setTab("restaurant_1")}>Spring Mtn <Count n={grouped.r1.length} /></UTab>
          <UTab active={tab === "restaurant_2"} onClick={() => setTab("restaurant_2")}>Henderson <Count n={grouped.r2.length} /></UTab>
        </div>
        <div className="flex-1" />
        <Button onClick={() => setDialog({ open: true, user: null })} className="bg-[#0B1F3A] hover:bg-[#11294A] text-white">
          <Plus size={16} className="mr-1.5" /> New user
        </Button>
      </div>

      <div className="space-y-6">
        {tab === "all" && grouped.main.length > 0 && (
          <Section title="Operations" users={grouped.main} currentUser={currentUser}
            onEdit={(u) => setDialog({ open: true, user: u })}
            onDelete={(u) => setConfirm(u)} />
        )}
        {showR1 && (
          <Section
            title={`${RESTAURANT_BY_ID.restaurant_1.brand} · ${RESTAURANT_BY_ID.restaurant_1.name}`}
            users={grouped.r1}
            currentUser={currentUser}
            onEdit={(u) => setDialog({ open: true, user: u })}
            onDelete={(u) => setConfirm(u)}
          />
        )}
        {showR2 && (
          <Section
            title={`${RESTAURANT_BY_ID.restaurant_2.brand} · ${RESTAURANT_BY_ID.restaurant_2.name}`}
            users={grouped.r2}
            currentUser={currentUser}
            onEdit={(u) => setDialog({ open: true, user: u })}
            onDelete={(u) => setConfirm(u)}
          />
        )}
      </div>

      <UserDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        user={dialog.user}
        onSave={save}
      />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#FBF8F1] border-[#E6E0D4]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-[#0B1F3A]">Remove {confirm?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This removes the user. Their past requests remain in the history.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#0B1F3A] hover:bg-[#11294A] text-white"
              onClick={() => { del(confirm.id); setConfirm(null); }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function Count({ n }) {
  return <span className="ml-1.5 text-[#0B1F3A]/50">{n}</span>;
}

function UTab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`h-9 px-3.5 rounded-md text-sm transition-colors ${active ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]/75 hover:bg-[#F3EFE7]"}`}
    >
      {children}
    </button>
  );
}

function Section({ title, users, currentUser, onEdit, onDelete }) {
  return (
    <div>
      <p className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/55 mb-2 px-1">{title}</p>
      <div className="bg-white border border-[#E6E0D4] rounded-xl divide-y divide-[#EEE8DC] overflow-hidden">
        {users.length === 0 ? (
          <div className="text-sm text-[#0B1F3A]/55 px-4 py-3">No users yet.</div>
        ) : (
          users.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              currentUser={currentUser}
              onEdit={() => onEdit(u)}
              onDelete={() => onDelete(u)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function UserRow({ user, currentUser, onEdit, onDelete }) {
  const isSelf = currentUser?.id === user.id;
  const isMainRow = user.role === ROLES.MAIN;
  const isMgr = user.role === ROLES.RESTAURANT;
  const areas = Array.isArray(user.areas) ? user.areas : user.area ? [user.area] : [];
  const accent = isMainRow || isMgr;
  return (
    <div className="flex items-center gap-3 md:gap-4 px-4 md:px-5 py-3">
      <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${accent ? "bg-[#F7E4DB] text-[#C4552B]" : "bg-[#EDEAE2] text-[#0B1F3A]"}`}>
        {isMainRow ? <ShieldCheck size={18} strokeWidth={1.8} /> : <UserCog size={18} strokeWidth={1.8} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-[#0B1F3A]">{user.name}</p>
          <Badge variant="outline" className="border-[#E6E0D4] text-[#0B1F3A]/70 bg-[#F3EFE7] text-[10px] tracking-eyebrow uppercase">
            {ROLE_LABEL[user.role]}
          </Badge>
          {areas.map((a) => (
            <Badge key={a} variant="outline" className="border-[#E6E0D4] text-[#0B1F3A]/70 bg-white">
              {AREA_BY_ID[a]?.name}
            </Badge>
          ))}
          {!user.active && (
            <Badge variant="outline" className="border-red-300 text-red-600 bg-red-50">Inactive</Badge>
          )}
        </div>
        <p className="text-xs text-[#0B1F3A]/60 mt-0.5 truncate">
          {user.email || "—"} · PIN {user.pinHash || user.pin ? "••••" : "—"}
        </p>
      </div>
      <div className="flex gap-1 shrink-0">
        <button onClick={onEdit} className="h-8 w-8 rounded-md hover:bg-[#F3EFE7] flex items-center justify-center text-[#0B1F3A]/70 hover:text-[#0B1F3A] transition-colors" title="Edit">
          <Edit3 size={14} />
        </button>
        <button
          disabled={isSelf}
          onClick={onDelete}
          className="h-8 w-8 rounded-md hover:bg-red-50 flex items-center justify-center text-[#0B1F3A]/60 hover:text-red-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title="Remove"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}
